<template>
  <div v-if="loading" class="left-0 top-0 h-full w-full py-20">
    <TheLoader />
  </div>
  <div v-else>
    <div v-if="isAuthorized">
      <ChartTab
        v-if="data.length"
        :subtitle="`${market.capacityName} ${CATHODE.name} Active Material Capacities`"
        :product="market.capacityName"
        :check-class-colors="checkClassColors"
        :circle-class-colors="circleClassColors"
        capacity-unit="tpa"
        :colors="colors"
        :hide-y-label="false"
        :is-lithium-ion-batteries="false"
        :truncate-labels="false"
        :raw-data="data"
        :regions="regions"
      />
    </div>

    <OverviewTab
      v-else
      :subtitle="`${market.capacityName} ${CATHODE.name} Active Material Capacities`"
      :descriptions="{
        title: null,
        texts: [
          'Precursor Cathode Active Material (pCAM) is the foundation for cathode active material (CAM), which is used in the production of cathodes for lithium ion batteries. ',
          'pCAM and CAM are essential to a successful transition towards e-mobility, with ultra-pure CAM being the battery’s most critical and therefore valuable component. ',
          'Understanding the plans of all current and emerging players in the lithium ion battery space is vital to evaluating the future of electric vehicles and reducing the world’s reliance on fossil fuels via energy storage.',
        ],
      }"
      :packages="{
        title: `${MARKET_ASSESSMENTS.singularName} Package`,
        services: [
          {
            icon: 'services/icon-chart-line',
            description:
              'Access to exclusive pCAM data, with company and regional capacity, and supply and demand/analytics.',
          },
          {
            icon: 'services/icon-report',
            description:
              'Monthly report providing pCAM insights and analysis to reveal the direction of the midstream battery sector.',
          },
          {
            icon: 'services/icon-global',
            description:
              'A global perspective on the evolution of the pCAM industry.',
          },
          {
            icon: 'services/icon-idea',
            description:
              'Our goal is to help you understand the context of our data, not just give you the numbers.',
          },
        ],
      }"
      :subscribe="{
        checkboxes,
        methodology: methodologyFileInfo,
      }"
    />
  </div>
</template>

<script setup lang="ts">
import ChartTab from '~/components/capacity-database/ChartTab.vue'
import OverviewTab from '~/components/capacity-database/OverviewTab.vue'
import {
  CATHODE,
  MARKET_ASSESSMENTS,
  PRECURSOR_ASSESSMENT,
} from '~/constants/services/constants'
import type { TFileDownloadInfo } from '~/src/types/download'
import { useAuthentication } from '~/composables/useAuthentication'
import { useCapacityDatabaseData } from '~/composables/useCapacityDatabaseData'
import {
  METHODOLOGY_FILE_INFO,
  CAPACITY_DATABASE_SUBSCRIPTIONS,
} from '~/src/constants/capacity-database/common'

/*-------------------------------------
  Meta
-------------------------------------*/
definePageMeta({ isTab: true })

/*-------------------------------------
  Constants: Chart Tab
-------------------------------------*/
const market = PRECURSOR_ASSESSMENT

const colors = ['#EA8000', '#FDAF00', '#FDD24C', '#FEEBB2']

const circleClassColors = [
  'bg-grey-900',
  'bg-grey-900',
  'bg-grey-900',
  'bg-grey-900',
]
const checkClassColors = [
  'text-grey-900',
  'text-grey-900',
  'text-grey-900',
  'text-grey-900',
]

/*-------------------------------------
  Constants: Overview Tab
-------------------------------------*/
const subscription = CAPACITY_DATABASE_SUBSCRIPTIONS[market.slug]
const checkboxes: string[] = [MARKET_ASSESSMENTS.checkbox]

const { $config } = useNuxtApp()

const METHODOLOGY_ENV_KEY = METHODOLOGY_FILE_INFO[market.slug].EnvKey
const methodologyFileInfo: TFileDownloadInfo = {
  ...METHODOLOGY_FILE_INFO[market.slug],
  ID: $config.public[METHODOLOGY_ENV_KEY],
}

/*-------------------------------------
  Props & Refs
-------------------------------------*/
const { loading, isAuthorized, data, regions, fetchChartDataIfAuthorized } =
  useCapacityDatabaseData({
    capacitySlug: market.capacitySlug,
    subscription,
  })

const emit = defineEmits<{
  'set-hero-ctas': [methodology: TFileDownloadInfo, checkboxes: string[]]
}>()

/*-------------------------------------
  Lifecycle
-------------------------------------*/
onMounted(() => {
  emit('set-hero-ctas', methodologyFileInfo, checkboxes)
  fetchChartDataIfAuthorized()
})

useAuthentication(() => {
  fetchChartDataIfAuthorized()
})

/*-------------------------------------
  SEO
-------------------------------------*/
const { getSeoInfo } = useSeo()
const seo = getSeoInfo({
  title: `${MARKET_ASSESSMENTS.name}: ${PRECURSOR_ASSESSMENT.capacityName}`,
  description: PRECURSOR_ASSESSMENT.seo.marketAssessments.capacities,
})
useHead(seo)
</script>
